import { Suspense, useLayoutEffect } from "react";
import { HashRouter, Route, Routes, useLocation } from "react-router-dom";
import { HomeScreen } from "./ui/screens/HomeScreen";
import { ROUTES } from "./ui/routes";
import { ScreenTransition } from "./ui/transitions/ScreenTransition";

/** Shown while a route chunk is in flight — deliberately quiet, since these loads are near-instant on a warm cache. */
function RouteFallback() {
  return (
    <div className="screen" style={{ justifyContent: "center" }}>
      <div className="hud-chip">Loading&hellip;</div>
    </div>
  );
}

/**
 * Every new screen starts at its top.
 *
 * `#root` is the one scroll container - the tray - and it outlives every route,
 * so a screen used to open at whatever depth the LAST screen had been scrolled
 * to: a menu reached from the bottom of a long list arrived part-way down. A
 * layout effect, so it lands before the new screen is painted; on the pathname
 * only, so a query change on the same screen (`?me=`) does not jump.
 */
function ScrollToTopOnRoute() {
  const { pathname } = useLocation();
  useLayoutEffect(() => {
    const root = document.getElementById("root");
    if (root) root.scrollTop = 0;
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

export function App() {
  return (
    <HashRouter>
      <ScrollToTopOnRoute />
      {/* A handle on whatever screen is mounted, for the sliding transition to
          take hold of.

          `display: contents`, so it generates no box at all and the layout is
          exactly what it was before this existed — which is the point. `.screen`
          sizes itself against the scrolling tray and `.play-screen` positions
          against it, so a real wrapper here would have to reproduce `#root`'s own
          box or quietly change both. The slide transforms this element's CHILD,
          never this. */}
      <div className="tx-stage">
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            {/* The home screen is the one route that is not code-split: it is
                what the first paint renders, so a chunk for it would only be a
                round trip in front of the app starting. Every other route's
                weight — Pixi above all — arrives with the route that needs it.
                See `ui/routes.ts`, which is also what a screen transition
                preloads from while the cards are covering. */}
            <Route path="/" element={<HomeScreen />} />
            {ROUTES.map(({ path, Component }) => (
              <Route key={path} path={path} element={<Component />} />
            ))}
          </Routes>
        </Suspense>
      </div>
      {/* Outside Suspense on purpose: the cards have to keep moving over a route
          that is still loading, which is most of what they are for. */}
      <ScreenTransition />
    </HashRouter>
  );
}
